// @ts-check

import { html } from "lit-html";
import { SECONDARY_BUTTON_CLASS } from "../ui.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createTimezonesState>} TimezonesState */

export const timezonesTool = {
  key: "timezones",
  path: "/timezones",
  title: "Timezones",
  blurb: "Compare your local time with Madrid, New York, and UTC.",
  badge: "Time",
  accentClass: "bg-gradient-to-r from-blue-300 via-indigo-300 to-violet-300",
  createState: createTimezonesState,
  render: renderTimezonesPage,
  /**
   * @param {TimezonesState} toolState
   * @returns {boolean}
   */
  shouldAutoRefresh: function (toolState) {
    return toolState.useCurrentTime;
  },
};

function createTimezonesState() {
  return {
    useCurrentTime: true,
    sliderValue: getQuarterSliderValue(new Date()),
  };
}

/**
 * @param {TimezonesState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderTimezonesPage(toolState, env) {
  const displayDate = toolState.useCurrentTime
    ? new Date()
    : createDateFromSliderValue(toolState.sliderValue);
  const sliderValue = toolState.useCurrentTime
    ? getQuarterSliderValue(new Date())
    : toolState.sliderValue;
  const madridTime = formatTimeForTimezone(displayDate, "Europe/Madrid");
  const newYorkTime = formatTimeForTimezone(displayDate, "America/New_York");
  const utcTime = formatTimeForTimezone(displayDate, "UTC");
  const sliderTimeLabel = formatTimeForZone(displayDate, undefined);

  return html`
    <section class="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] xl:items-start">
      <div class="grid gap-4">
        <div class="rounded-lg bg-stone-100 p-5 text-center">
          <div class="text-5xl font-semibold tracking-tight text-stone-900">${formatTimeForZone(displayDate, undefined)}</div>
          <div class="mt-3 text-base text-stone-500">${formatDateForZone(displayDate, undefined)}</div>
        </div>

        <div class="rounded-lg bg-stone-100 p-4">
          <div class="flex items-center justify-between gap-4">
            <label class="text-sm font-medium text-stone-700" for="time-slider">Adjust time</label>
            <button
              class=${SECONDARY_BUTTON_CLASS}
              type="button"
              @click=${function () {
                toolState.useCurrentTime = true;
                toolState.sliderValue = getQuarterSliderValue(new Date());
                env.scheduleRender();
              }}
            >
              Reset to current time
            </button>
          </div>

          <input
            id="time-slider"
            class="mt-4 h-2 w-full cursor-pointer accent-stone-700"
            type="range"
            min="0"
            max="95"
            step="1"
            .value=${String(sliderValue)}
            @input=${
              /**
               * @param {Event} event
               * @returns {void}
               */
              function (event) {
                const target = event.target;
                if (!(target instanceof HTMLInputElement)) {
                  return;
                }

                toolState.useCurrentTime = false;
                toolState.sliderValue = Number(target.value);
                env.scheduleRender();
              }
            }
          />

          <div class="mt-3 flex justify-between text-xs text-stone-400">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>23:45</span>
          </div>
          <div class="mt-3 text-sm text-stone-600">Selected time: ${sliderTimeLabel}</div>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-2 md:items-start">
        ${renderTimezoneCard("Your Local Time", formatTimeForZone(displayDate, undefined), formatDateForZone(displayDate, undefined), getTimezoneOffsetLabel(displayDate))}
        ${renderTimezoneCard("Madrid, Spain", madridTime.time, madridTime.date, "CET / CEST")}
        ${renderTimezoneCard("New York, USA", newYorkTime.time, newYorkTime.date, "EST / EDT")}
        ${renderTimezoneCard("UTC", utcTime.time, utcTime.date, "Coordinated Universal Time")}
      </div>
    </section>
  `;
}

/**
 * @param {string} title
 * @param {string} value
 * @param {string} subtitle
 * @param {string} caption
 * @returns {LitView}
 */
function renderTimezoneCard(title, value, subtitle, caption) {
  return html`
    <section class="rounded-lg bg-stone-100 p-5">
      <h3 class="text-sm font-semibold text-stone-500">${title}</h3>
      <div class="mt-3 text-3xl font-semibold tracking-tight text-stone-900">${value}</div>
      <div class="mt-2 text-sm text-stone-600">${subtitle}</div>
      <div class="mt-1.5 text-xs uppercase tracking-widest text-stone-400">${caption}</div>
    </section>
  `;
}

/**
 * @param {Date} date
 * @returns {number}
 */
function getQuarterSliderValue(date) {
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  return Math.floor(totalMinutes / 15);
}

/**
 * @param {number} sliderValue
 * @returns {Date}
 */
function createDateFromSliderValue(sliderValue) {
  const today = new Date();
  const totalMinutes = sliderValue * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    hours,
    minutes,
    0,
    0,
  );
}

/**
 * @param {Date} date
 * @param {string | undefined} timeZone
 * @returns {string}
 */
function formatTimeForZone(date, timeZone) {
  return date.toLocaleTimeString("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * @param {Date} date
 * @param {string | undefined} timeZone
 * @returns {string}
 */
function formatDateForZone(date, timeZone) {
  return date.toLocaleDateString("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {{ time: string, date: string }}
 */
function formatTimeForTimezone(date, timeZone) {
  return {
    time: formatTimeForZone(date, timeZone),
    date: formatDateForZone(date, timeZone),
  };
}

/**
 * @param {Date} date
 * @returns {string}
 */
function getTimezoneOffsetLabel(date) {
  const offset = date.getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset <= 0 ? "+" : "-";
  return `UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
